import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import styles from './person-information-section.module.css';
import clsx from 'clsx';
import { Button } from '../button';
import {
  GetPersonalInfoResponse,
  ImageSize,
  UpdateAvatarErrorResponse,
  UpdateAvatarErrorType,
} from '@/api/api.schemas';
import { useGetPatientAvatar } from '@/api/get-patient-avatar';
import { PersonDetailsSection } from './person-details-section';
import { PersonAttributesSection } from './person-attributes-section';
import { useState } from 'react';
import { Banner } from '../banner';

interface PatientInformationProps {
  patient: GetPersonalInfoResponse | undefined;
  isFetchingPatient: boolean;
  canEdit: boolean;
}

export function PatientInformation({
  patient,
  isFetchingPatient,
  canEdit,
}: PatientInformationProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const {
    data: patientAvatar,
    isFetching: isFetchingPatientAvatar,
    refetch: refetchAvatar,
  } = useGetPatientAvatar({
    size: ImageSize.Thumb,
  });

  if (isFetchingPatient) {
    return <Loader type="small" />;
  }

  const onError = (error?: UpdateAvatarErrorResponse) => {
    if (!error) {
      setError(undefined);
      return;
    }

    if (
      error.type === UpdateAvatarErrorType.InvalidImage ||
      error.type === UpdateAvatarErrorType.ValidationFailed
    ) {
      setError(t('patientInformation.patient.fileTypeNotSupported'));
      return;
    }

    setError(t('common.errors.unknown'));
  };

  const isProfileCompleted = patient && patient.dateOfBirth;

  return (
    <div className={styles.section}>
      <div className={styles.titleSection}>
        <p className={clsx('body-1', 'bold')}>{t('patientInformation.patient.title')}</p>
        {isProfileCompleted && canEdit && (
          <Button variant="link" size="S" leftIcon="edit" as="a" to="/profile/edit-patient-info">
            {t('patientInformation.editDetails')}
          </Button>
        )}
      </div>
      {error && <Banner type="error">{error}</Banner>}

      {isProfileCompleted ? (
        <div className={styles.detailsContainer}>
          <PersonDetailsSection
            variant="patient"
            person={patient}
            avatar={patientAvatar}
            isLoadingAvatar={isFetchingPatientAvatar}
            onUploadPhoto={refetchAvatar}
            onError={onError}
          />

          <PersonAttributesSection
            address1={patient.mailingAddress1}
            address2={patient.mailingAddress2}
            city={patient.city}
            state={patient.state}
            zip={patient.zip}
            email={patient.email}
            cellPhone={patient.cellPhone}
            homePhone={patient.homePhone}
          />
        </div>
      ) : (
        <PatientInformationEmptySection />
      )}
    </div>
  );
}

function PatientInformationEmptySection() {
  const { t } = useTranslation();

  return (
    <div className={styles.emptySection}>
      <p className={clsx('body-1', 'bold')}>{t('patientInformation.emptySection.patient.title')}</p>
      <Button size="M" as="a" to="/profile/type-selection">
        {t('patientInformation.emptySection.patient.buttonTxt')}
      </Button>
    </div>
  );
}
